import React, { useMemo, useState } from "react";

const toYmd = (d) => {
  if (!d) return "";
  // Accept Date or string
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toISOString().slice(0, 10);
};

const sanitizeRow = (r) => {
  const DateStr = (r?.Date || "").trim();
  const PriceNum = Number(String(r?.Price ?? "").replace(/[^\d.-]/g, ""));
  if (!DateStr) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(DateStr)) return null;
  if (!Number.isFinite(PriceNum) || PriceNum <= 0) return null;
  return { Date: DateStr, Price: Math.round(PriceNum) };
};

const dedupeSort = (rows) => {
  const cleaned = rows.map(sanitizeRow).filter(Boolean);

  const seen = new Set();
  const uniq = [];
  for (const r of cleaned) {
    const key = `${r.Date}|${r.Price}`;
    if (seen.has(key)) continue;
    seen.add(key);
    uniq.push(r);
  }

  // newest first
  uniq.sort((a, b) => (a.Date < b.Date ? 1 : a.Date > b.Date ? -1 : 0));
  return uniq;
};

export default function PriceHistoryModal({
  open,
  initialHistory = [],
  onClose,
  onSave,
  title = "Edit price history",
}) {
  const [rows, setRows] = useState(() =>
    (Array.isArray(initialHistory) ? initialHistory : []).map((h) => ({
      Date: toYmd(h?.Date),
      Price: h?.Price ?? "",
    }))
  );

  const preview = useMemo(() => dedupeSort(rows), [rows]);

  if (!open) return null;

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const addRow = () => setRows((prev) => [{ Date: "", Price: "" }, ...prev]);
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    const payload = dedupeSort(rows);
    await onSave(payload); // parent does API call
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 5000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onMouseDown={onClose}
    >
      <div
        style={{
          width: "min(760px, 100%)",
          background: "#fff",
          borderRadius: 12,
          padding: 16,
          boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <div style={{ fontWeight: 800, fontSize: 16 }}>{title}</div>
          <button onClick={onClose} style={{ fontSize: 16 }}>✕</button>
        </div>

        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
          <button onClick={addRow}>+ Add entry</button>
          <div style={{ marginLeft: "auto", opacity: 0.7, fontSize: 12 }}>
            Stored as Date (YYYY-MM-DD) + Price (GBP)
          </div>
        </div>

        <div style={{ marginTop: 12, maxHeight: "50vh", overflow: "auto" }}>
          {rows.length === 0 && (
            <div style={{ opacity: 0.7, padding: 8 }}>No entries yet.</div>
          )}

          {rows.map((r, idx) => (
            <div
              key={idx}
              style={{
                display: "grid",
                gridTemplateColumns: "160px 1fr 44px",
                gap: 10,
                alignItems: "center",
                padding: "8px 0",
                borderBottom: "1px solid #eee",
              }}
            >
              <input
                type="date"
                value={r.Date}
                onChange={(e) => updateRow(idx, { Date: e.target.value })}
              />
              <input
                type="text"
                inputMode="numeric"
                placeholder="e.g. 325000"
                value={r.Price}
                onChange={(e) => updateRow(idx, { Price: e.target.value })}
              />
              <button onClick={() => removeRow(idx)} title="Remove">🗑️</button>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 12, opacity: 0.8, fontSize: 13 }}>
          Preview (deduped + newest first):{" "}
          <strong>{preview.length}</strong> entries
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
          <button onClick={onClose}>Cancel</button>
          <button onClick={handleSave} style={{ fontWeight: 800 }}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
